import { Card, ThemeIcon, UnstyledButton } from '@mantine/core'
import { useHover, useMergedRef } from '@mantine/hooks'
import { IconCheck } from '@tabler/icons-react'
import { KeyboardEvent, ReactNode, forwardRef } from 'react'

const CARD_HEIGHT = 160

type SelectableCardProps = {
  children: ReactNode
  isSelected?: boolean
  disabled?: boolean
  height?: number
  renderAs?: 'div' | 'button'
  onClick?: () => void
}

export const SelectableCard = forwardRef<HTMLDivElement, SelectableCardProps>(
  (
    {
      children,
      isSelected = false,
      disabled = false,
      height,
      renderAs = 'button',
      onClick
    },
    ref
  ) => {
    const { hovered, ref: hoverRef } = useHover()
    const cardShadow = hovered ? 'md' : 'xs'

    // Merge forwarded ref with hover ref
    const mergedRef = useMergedRef(hoverRef, ref)

    const border = isSelected
      ? '1px solid var(--mantine-color-green-6)'
      : '1px solid var(--mantine-color-gray-5)'

    const outline = isSelected
      ? '2px solid var(--mantine-color-green-6)'
      : 'none'

    const disabledStyles = {
      opacity: 0.5,
      cursor: 'not-allowed'
    }

    const cardStyles = {
      border,
      outline,
      cursor: onClick === undefined ? 'default' : 'pointer',
      ...(disabled ? disabledStyles : {})
    }

    const handleClick = () => {
      if (!disabled) {
        onClick?.()
      }
    }

    // renderAs="div" is chosen by callers that nest another interactive
    // element (e.g. a delete button) inside the card, which HTML forbids
    // inside a real <button>. UnstyledButton then renders a plain <div>,
    // which loses native focus/keyboard-activation — restore it by hand so
    // the card stays operable for keyboard users.
    const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
      // A nested interactive element (e.g. the delete button) bubbles its
      // own keydown up to us — ignore it so Enter/Space on that control
      // doesn't also activate the card underneath it.
      if (event.target !== event.currentTarget) return
      if (event.key !== 'Enter' && event.key !== ' ') return
      event.preventDefault()
      handleClick()
    }

    const nonButtonProps =
      renderAs === 'div'
        ? {
            role: 'button' as const,
            tabIndex: disabled ? -1 : 0,
            'aria-disabled': disabled,
            onKeyDown: handleKeyDown
          }
        : {}

    return (
      <UnstyledButton
        component={renderAs}
        w="100%"
        onClick={handleClick}
        {...nonButtonProps}
      >
        <Card
          ref={mergedRef}
          shadow={cardShadow}
          h={height ?? CARD_HEIGHT}
          padding="md"
          radius="md"
          pos="relative"
          style={cardStyles}
          withBorder
        >
          {children}

          {isSelected && (
            <ThemeIcon
              color="green"
              size="sm"
              radius="xl"
              pos="absolute"
              top={4}
              left={4}
              styles={{
                root: {
                  outline: '2px solid var(--mantine-color-white)'
                }
              }}
            >
              <IconCheck style={{ width: '80%', height: '80%' }} stroke={3} />
            </ThemeIcon>
          )}
        </Card>
      </UnstyledButton>
    )
  }
)

SelectableCard.displayName = 'SelectableCard'
